import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { RECORDS_REQUEST_EVAL_ALLOWED_EMAILS } from '@/components/agentflow/RecordsRequestStatusLine';
import { TableAction } from '@/components/common/DataTable/types';
import {
  buildTreatmentCheckInComment,
  requiresBulkDueDateChangeReason,
  requiresBulkStatusChangeReason,
  TREATMENT_CHECK_IN_CHECKBOXES,
  TREATMENT_CHECK_IN_PROTECTED_STATUSES,
  TREATMENT_CHECK_IN_STATUS_WARNING,
} from '@/constants/task-field-change-reasons';
import { useFieldChangeReason } from '@/contexts/FieldChangeReasonContext';
import { usePushChanges } from '@/contexts/PushChangesContext';
import { usePostHogCapture } from '@/hooks/usePostHogCapture';
import { filterStaffUsers } from '@/lib/utils';
import { useGetTaskStatusesQuery } from '@/services/api';
import { useStartRecordsRequestEvaluationMutation } from '@/services/api/agentflowService';
import { useGetFirmQuery } from '@/services/api/firmsService';
import {
  BulkUpdateSkipReason,
  SPECIAL_ASSIGNEE_VALUES,
  useBulkUpdateTasksMutation,
} from '@/services/api/tasksService';
import { useGetStaffUsersQuery } from '@/services/api/userService';
import { Task } from '@/services/types/client-intake-types';
import {
  CanonicalTaskStatus,
  CanonicalTaskType,
} from '@/services/types/task-types';
import { selectEmail } from '@/state/selectors/root-selectors';
import { createConsistentDate } from '@/utils/date';

import { BulkDatePickerAction } from '../components/BulkDatePickerAction';
import { useTaskVoiceAIBulkAction } from './useTaskVoiceAIBulkAction';

/**
 * Human-readable messages for bulk update skip reasons.
 */
const SKIP_REASON_MESSAGES: Record<BulkUpdateSkipReason, string> = {
  task_not_found: 'Task not found',
  no_managing_paralegal: 'No managing paralegal assigned to firm',
  no_case_manager: 'No case manager assigned to matter',
  invalid_assignee_id: 'Invalid assignee',
  invalid_assignee_value: 'Invalid assignee value',
  unassign_not_allowed: 'Bulk unassign is not allowed',
  trr_attachments_required:
    'Treatment records tasks need file selection before completion',
};

interface UseTaskBulkActionsProps {
  tasks: Task[];
  selectedRows: Set<string>;
  setSelectedRows: (rows: Set<string>) => void;
}

export const useTaskBulkActions = ({
  tasks,
  selectedRows,
  setSelectedRows,
}: UseTaskBulkActionsProps) => {
  const [copySuccess, setCopySuccess] = useState(false);
  const captureEvent = usePostHogCapture();

  // Field change reason context for capturing reasons during bulk updates
  const { requestBulkChangeReason } = useFieldChangeReason();

  // Determine the common firm ID from selected tasks (if all tasks share the same firm)
  const commonFirmId = useMemo(() => {
    if (selectedRows.size === 0) return null;

    const selectedTasks = tasks.filter((t) => selectedRows.has(t.id));
    if (selectedTasks.length === 0) return null;

    const firmIds = new Set(selectedTasks.map((t) => t.firm));
    // Only return a firm ID if ALL selected tasks belong to the same firm
    return firmIds.size === 1 ? selectedTasks[0].firm : null;
  }, [tasks, selectedRows]);

  // Fetch the firm's managing paralegal (only when all selected tasks share the same firm)
  // Note: When skip is true, the firmId param is not used - we provide empty string as fallback
  const { data: firmData } = useGetFirmQuery(commonFirmId ?? '', {
    skip: !commonFirmId,
  });
  // Important: Only use firmData when commonFirmId is set, as RTK Query may return
  // cached data even when the query is skipped. This prevents stale MP data from showing
  // when selection changes from single-firm to multi-firm tasks.
  const managingParalegal = commonFirmId
    ? (firmData?.managing_paralegal ?? null)
    : null;

  // API Hooks
  const { data: staffUsers = [] } = useGetStaffUsersQuery();
  const filteredStaffUsers = useMemo(
    () => filterStaffUsers(staffUsers),
    [staffUsers],
  );
  const { data: taskStatuses = [] } = useGetTaskStatusesQuery();
  const [bulkUpdateTasks] = useBulkUpdateTasksMutation();

  // Voice AI Hook
  const { triggerVoiceAI, isLoading: isVoiceAILoading } =
    useTaskVoiceAIBulkAction();

  // Records Request Evaluation
  const userEmail = useSelector(selectEmail);
  const isEvalAllowed = Boolean(
    userEmail && RECORDS_REQUEST_EVAL_ALLOWED_EMAILS.has(userEmail),
  );
  const [startRecordsRequestEval, { isLoading: isEvalLoading }] =
    useStartRecordsRequestEvaluationMutation();

  // Push Changes Hook for syncing to external task systems
  const { pushChangesMultiple } = usePushChanges();

  // --- Handlers ---

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {
      setCopySuccess(false);
    }
  };

  const handleBulkUpdate = useCallback(
    async (
      taskIds: string[],
      updates: {
        assignee?: string | null;
        status_id?: string;
        due_date?: string;
      },
    ) => {
      let changeReason: string | undefined;

      // Look up the new status once (used for both reason check and push changes)
      const newStatus = updates.status_id
        ? taskStatuses.find((s) => s.id === updates.status_id)
        : undefined;

      const selectedTasks = tasks.filter((t) => taskIds.includes(t.id));
      const hasAnyTreatmentCheckIn = selectedTasks.some((t) => {
        const ct =
          typeof t.type === 'object' ? t.type?.canonical_type : undefined;
        if (ct && ct !== CanonicalTaskType.TREATMENT_CHECK_IN) return false;
        return (
          ct === CanonicalTaskType.TREATMENT_CHECK_IN ||
          t.name?.toLowerCase().includes('treatment check')
        );
      });

      const isTreatmentCheckInProtected =
        hasAnyTreatmentCheckIn &&
        newStatus &&
        TREATMENT_CHECK_IN_PROTECTED_STATUSES.includes(
          newStatus.canonical_status as CanonicalTaskStatus,
        );

      let isTreatmentCheckInReason = false;
      let treatmentCheckInNote: string | undefined;

      if (isTreatmentCheckInProtected && newStatus) {
        const result = await requestBulkChangeReason({
          fieldType: 'status',
          newValue: newStatus.name,
          taskCount: taskIds.length,
          warningMessage: TREATMENT_CHECK_IN_STATUS_WARNING,
          requiredCheckboxes: TREATMENT_CHECK_IN_CHECKBOXES,
        });
        if (!result) return;
        changeReason = result.reasonDetail || result.reason;
        treatmentCheckInNote = result.reasonDetail;
        isTreatmentCheckInReason = true;
      } else if (
        newStatus &&
        requiresBulkStatusChangeReason(newStatus.canonical_status)
      ) {
        const result = await requestBulkChangeReason({
          fieldType: 'status',
          newValue: newStatus.name,
          taskCount: taskIds.length,
        });
        if (!result) return;
        changeReason = result.reasonDetail || result.reason;
      }

      // Check if due date change requires a reason (always for bulk)
      if (updates.due_date && requiresBulkDueDateChangeReason()) {
        const result = await requestBulkChangeReason({
          fieldType: 'due_date',
          newValue: updates.due_date,
          taskCount: taskIds.length,
        });
        if (!result) return; // User cancelled - abort entire operation
        changeReason = result.reasonDetail || result.reason;
      }

      // Determine the bulk action type for tracking
      const getBulkActionType = () => {
        if ('assignee' in updates) return 'assignment';
        if ('status_id' in updates) return 'status';
        if ('due_date' in updates) return 'due_date';
        return 'unknown';
      };
      const bulkActionType = getBulkActionType();

      captureEvent(`bulk_action_${bulkActionType}`, {
        page: 'tasks',
        component: 'task_toolbar',
        task_count: taskIds.length,
        action_type: bulkActionType,
      });

      const finalChangeReason = isTreatmentCheckInReason
        ? buildTreatmentCheckInComment(treatmentCheckInNote)
        : changeReason;

      try {
        const response = await bulkUpdateTasks({
          task_ids: taskIds,
          ...updates,
          change_reason: finalChangeReason,
        }).unwrap();

        const { updated_count, skipped } = response;
        const skippedCount = skipped.length;

        if (skippedCount === 0 && updated_count > 0) {
          toast.success(
            `Successfully updated ${updated_count} task${updated_count !== 1 ? 's' : ''}`,
          );
        } else if (updated_count > 0 && skippedCount > 0) {
          // Group skipped tasks by reason for better error messaging
          const reasonCounts = skipped.reduce(
            (acc, item) => {
              acc[item.reason] = (acc[item.reason] || 0) + 1;
              return acc;
            },
            {} as Record<BulkUpdateSkipReason, number>,
          );

          const skipReasonSummary = Object.entries(reasonCounts)
            .map(
              ([reason, count]) =>
                `${count} ${SKIP_REASON_MESSAGES[reason as BulkUpdateSkipReason]}`,
            )
            .join(', ');

          toast.warning(
            `Updated ${updated_count} task${updated_count !== 1 ? 's' : ''}, skipped ${skippedCount}: ${skipReasonSummary}`,
          );
        } else if (updated_count === 0 && skippedCount > 0) {
          // All tasks were skipped
          const firstReason = skipped[0].reason;
          toast.error(
            `Failed to update tasks: ${SKIP_REASON_MESSAGES[firstReason]}`,
          );
        } else {
          // No updates, no skips (shouldn't happen normally)
          toast.info('No tasks were updated');
        }

        // Push changes to external task systems (Filevine, Asana, etc.)
        // Only for status and due_date changes (assignment has no external sync)
        if (updated_count > 0 && (updates.status_id || updates.due_date)) {
          const skippedIds = new Set(skipped.map((s) => s.task_id));
          const updatedTaskIds = taskIds.filter((id) => !skippedIds.has(id));

          if (updatedTaskIds.length > 0) {
            // Use Map for O(1) lookups instead of O(n) find per task
            const taskMap = new Map(tasks.map((t) => [t.id, t]));
            const internalTasks = updatedTaskIds
              .map((id) => {
                const task = taskMap.get(id);
                return task ? { id: task.id, name: task.name } : null;
              })
              .filter((t): t is { id: string; name: string } => t !== null);

            const changesToPush: Array<{
              changeType: 'status' | 'due_date' | 'comment';
              changeData: { status?: string; due_date?: string; comment?: string };
            }> = [];

            // Reuse newStatus from earlier lookup
            if (newStatus) {
              changesToPush.push({
                changeType: 'status',
                changeData: { status: newStatus.canonical_status },
              });
            }

            if (updates.due_date) {
              changesToPush.push({
                changeType: 'due_date',
                changeData: { due_date: updates.due_date },
              });
            }

            if (finalChangeReason) {
              changesToPush.push({
                changeType: 'comment',
                changeData: { comment: finalChangeReason },
              });
            }

            if (changesToPush.length > 0 && internalTasks.length > 0) {
              // Separate try/catch - bulk update already succeeded, don't fail on push error
              try {
                await pushChangesMultiple({
                  internalTasks,
                  changes: changesToPush,
                });
              } catch (pushError) {
                console.error(
                  'Failed to push changes to external systems:',
                  pushError,
                );
                // Tasks updated successfully, just warn about external sync failure
                toast.warning(
                  'Tasks updated, but failed to sync with external systems.',
                );
              }
            }
          }
        }
      } catch {
        toast.error('Failed to update tasks. Please try again.');
      }

      setSelectedRows(new Set());
    },
    [
      taskStatuses,
      bulkUpdateTasks,
      setSelectedRows,
      captureEvent,
      requestBulkChangeReason,
      tasks,
      pushChangesMultiple,
    ],
  );

  const handleBulkDueDateChange = useCallback(
    (date: Date) => {
      const taskIds = Array.from(selectedRows);
      handleBulkUpdate(taskIds, { due_date: createConsistentDate(date) });
    },
    [selectedRows, handleBulkUpdate],
  );

  /**
   * Unified Voice AI Call handler that routes based on task data.
   */
  const handleBulkTriggerVoiceAI = useCallback(async () => {
    const selectedTaskIds = Array.from(selectedRows);
    const selectedTasks = tasks.filter((t) => selectedTaskIds.includes(t.id));

    captureEvent('bulk_action_voice_ai', {
      page: 'tasks',
      component: 'task_toolbar',
      task_count: selectedTasks.length,
      action_type: 'voice_ai',
    });

    await triggerVoiceAI(selectedTasks);
    setSelectedRows(new Set());
  }, [selectedRows, tasks, triggerVoiceAI, setSelectedRows, captureEvent]);

  const handleBulkEvaluatePackage = useCallback(async () => {
    const selectedTaskIds = Array.from(selectedRows);
    const selectedTasks = tasks.filter((t) => selectedTaskIds.includes(t.id));

    // Filter to medical records tasks that have a treatment_record_request
    const eligibleTasks = selectedTasks.filter((t) => {
      const ct =
        typeof t.type === 'object' ? t.type?.canonical_type : undefined;
      return (
        ct === CanonicalTaskType.MEDICAL_RECORDS &&
        t.treatment_record_request
      );
    });

    if (eligibleTasks.length === 0) {
      toast.error('No eligible medical records tasks selected');
      return;
    }

    captureEvent('bulk_action_evaluate_package', {
      page: 'tasks',
      component: 'task_toolbar',
      task_count: eligibleTasks.length,
      action_type: 'evaluate_package',
    });

    const results = await Promise.allSettled(
      eligibleTasks.map((task) =>
        startRecordsRequestEval({
          matterId: task.matter,
          requestId: task.treatment_record_request!.id,
        }).unwrap(),
      ),
    );

    const succeeded = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.filter((r) => r.status === 'rejected').length;

    if (failed === 0) {
      toast.success(
        `Started evaluation for ${succeeded} task${succeeded !== 1 ? 's' : ''}`,
      );
    } else if (succeeded > 0) {
      toast.warning(
        `Started ${succeeded}, failed ${failed} evaluation${failed !== 1 ? 's' : ''}`,
      );
    } else {
      toast.error('Failed to start evaluations');
    }

    setSelectedRows(new Set());
  }, [
    selectedRows,
    tasks,
    startRecordsRequestEval,
    setSelectedRows,
    captureEvent,
  ]);

  // --- Action Configurations ---

  const renderDatePickerAction = useCallback(
    (count: number) => (
      <BulkDatePickerAction
        selectedCount={count}
        onDateSelect={handleBulkDueDateChange}
      />
    ),
    [handleBulkDueDateChange],
  );

  const bulkActions = useMemo(() => {
    const actions: TableAction[] = [
      // 1. Change Assignment
      {
        label: 'Change Assignment',
        dropdown: [
          // Managing Paralegal option - first
          // Uses special value that the backend resolves per-task
          {
            label: managingParalegal
              ? `Assign to MP (${managingParalegal.first_name} ${managingParalegal.last_name})`
              : 'Assign to Managing Paralegal',
            value: SPECIAL_ASSIGNEE_VALUES.MANAGING_PARALEGAL,
            onClick: () =>
              handleBulkUpdate(Array.from(selectedRows), {
                assignee: SPECIAL_ASSIGNEE_VALUES.MANAGING_PARALEGAL,
              }),
          },
          // Case Manager option - uses special value that backend resolves per-task
          {
            label: 'Assign to Case Manager',
            value: SPECIAL_ASSIGNEE_VALUES.CASE_MANAGER,
            onClick: () =>
              handleBulkUpdate(Array.from(selectedRows), {
                assignee: SPECIAL_ASSIGNEE_VALUES.CASE_MANAGER,
              }),
          },
          // Staff users
          ...filteredStaffUsers.map((user) => ({
            label: `${user.first_name} ${user.last_name}`,
            value: user.id,
            onClick: () =>
              handleBulkUpdate(Array.from(selectedRows), {
                assignee: user.id,
              }),
          })),
        ],
      },
      // 2. Change Status
      {
        label: 'Change Status',
        dropdown: taskStatuses.map((status) => ({
          label: status.name,
          value: status.id,
          onClick: () =>
            handleBulkUpdate(Array.from(selectedRows), {
              status_id: status.id,
            }),
        })),
      },
      // 3. Change Next Action Date
      {
        label: 'Change Next Action Date',
        customRender: renderDatePickerAction,
      },
      // 4. Trigger Voice AI Call (available for all verticals)
      {
        label: isVoiceAILoading
          ? 'Starting Voice AI...'
          : 'Trigger Voice AI Call',
        onClick: isVoiceAILoading ? undefined : handleBulkTriggerVoiceAI,
        variant: 'outline',
      },
    ];

    // 5. Evaluate Package (allowlisted users only)
    if (isEvalAllowed) {
      actions.push({
        label: isEvalLoading
          ? 'Running Agent...'
          : 'Run Agent',
        onClick: isEvalLoading ? undefined : handleBulkEvaluatePackage,
        variant: 'outline',
      });
    }

    return actions;
  }, [
    selectedRows,
    filteredStaffUsers,
    taskStatuses,
    handleBulkUpdate,
    renderDatePickerAction,
    handleBulkTriggerVoiceAI,
    isVoiceAILoading,
    managingParalegal,
    isEvalAllowed,
    isEvalLoading,
    handleBulkEvaluatePackage,
  ]);

  return {
    copySuccess,
    handleShare,
    bulkActions,
    handleBulkUpdate, // Exported in case needed elsewhere
  };
};
